<script lang="ts">
	import { onMount, createEventDispatcher } from 'svelte';
	import { Button, Modal, Tabs, Tab } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		placedFurnitures,
		selectedFurnitureId,
		furnituresVisible,
		selectFurniture,
		deletePlacedFurniture,
		updatePlacedFurniture,
		toggleFurnituresVisibility,
		resetDroppedFurnitures,
		uniqueName,
		makeFurnitureId,
		addPlacedFurniture,
	} from 'src/stores/features/radar/furniture';
	import { loadFurnitureCatalog, type FurnitureGroup } from '../helpers/furniture-catalog';
	import { getDefaultFracs } from '../helpers/furnitures-helper';

	const dispatch = createEventDispatcher<{
		/** Emitted when user wants to place a new item at center. */
		placeFurniture: { src: string; name: string; wFrac: number; hFrac: number };
	}>();

	// ── Tab state ─────────────────────────────────────────────────────────────
	let activeTab: 'placed' | 'add' = 'placed';
	let selectedGroup: FurnitureGroup | null = null;
	let catalogGroups: FurnitureGroup[] = [];
	let catalogLoading = true;

	// ── Modal state ───────────────────────────────────────────────────────────
	let showResetModal = false;

	onMount(async () => {
		catalogGroups = await loadFurnitureCatalog();
		catalogLoading = false;
	});

	// ── Helpers ───────────────────────────────────────────────────────────────

	function switchTab(tab: 'placed' | 'add') {
		activeTab = tab;
		if (tab === 'placed') selectedGroup = null;
	}

	function handleGroupClick(group: FurnitureGroup) {
		selectedGroup = group;
	}

	function handleBackClick() {
		selectedGroup = null;
	}

	function handleItemClick(src: string, displayName: string) {
		const { wFrac, hFrac } = getDefaultFracs(src);
		const name = uniqueName(displayName);
		const xFrac = Math.max(0, 0.5 - wFrac / 2);
		const yFrac = Math.max(0, 0.5 - hFrac / 2);

		addPlacedFurniture({
			id: makeFurnitureId(),
			src,
			name,
			xFrac,
			yFrac,
			wFrac,
			hFrac,
			rotation: 0,
		});

		// Switch back to Placed tab so user sees the result
		switchTab('placed');
	}

	function handlePlacedItemClick(id: string) {
		selectFurniture($selectedFurnitureId === id ? null : id);
	}

	function rotateSelected() {
		const item = $placedFurnitures.find((f) => f.id === $selectedFurnitureId);
		if (!item) return;
		updatePlacedFurniture({ ...item, rotation: ((item.rotation || 0) + 90) % 360 });
	}

	function deleteSelected() {
		if ($selectedFurnitureId) deletePlacedFurniture($selectedFurnitureId);
	}

	function confirmReset() {
		resetDroppedFurnitures();
		showResetModal = false;
	}

	$: selectedItem = $placedFurnitures.find((f) => f.id === $selectedFurnitureId) ?? null;
</script>

<div class="furnitures">
	<Tabs useNewLayout={true} class="mb-s">
		<Tab
			active={activeTab === 'placed'}
			useNewLayout={true}
			hideRightSlot={true}
			on:click={() => switchTab('placed')}
		>
			{$placedFurnitures.length
				? $format('presence_furniture_tab_placed_count', {
						values: { count: $placedFurnitures.length },
						default: `Placed (${$placedFurnitures.length})`,
					})
				: $format('presence_furniture_tab_placed', { default: 'Placed' })}
		</Tab>
		<Tab
			active={activeTab === 'add'}
			useNewLayout={true}
			hideRightSlot={true}
			on:click={() => switchTab('add')}
		>
			{$format('presence_furniture_tab_add', { default: 'Add' })}
		</Tab>
	</Tabs>

	<div class="furnitures__content">
		<!-- ── PLACED TAB ──────────────────────────────────────────────────── -->
		{#if activeTab === 'placed'}
			{#if $placedFurnitures.length > 0}
				<ul class="placed-list" role="list" aria-label="Placed furniture">
					{#each $placedFurnitures as item (item.id)}
						{@const isSelected = $selectedFurnitureId === item.id}
						<li>
							<button
								class="placed-item"
								class:placed-item--selected={isSelected}
								on:click={() => handlePlacedItemClick(item.id)}
								aria-pressed={isSelected}
								aria-label="Select {item.name}"
							>
								<div class="placed-item__thumb">
									<img
										src="./images/furnitures/{item.src}"
										alt=""
										class="placed-item__img"
									/>
								</div>
								<span class="placed-item__name">{item.name}</span>
								{#if item.rotation}
									<span class="placed-item__rotation" title="Rotation"
										>{item.rotation.toFixed(2)}°</span
									>
								{/if}
							</button>
						</li>
					{/each}
				</ul>

				<!-- Selected item actions -->
				{#if selectedItem}
					<div class="selected-actions">
						<span class="selected-actions__label">{selectedItem.name}</span>
						<button
							class="action-btn"
							on:click={rotateSelected}
							aria-label="Rotate 90°"
							title="Rotate 90°"
						>
							<i class="fas fa-rotate-right" aria-hidden="true"></i>
							{$format('button_rotate', { default: 'Rotate' })}
						</button>
						<button
							class="action-btn action-btn--danger"
							on:click={deleteSelected}
							aria-label={$format('presence_furniture_delete_tooltip', {
								default: 'Delete furniture',
							})}
							title={$format('button_delete', { default: 'Delete' })}
						>
							<i class="fas fa-trash" aria-hidden="true"></i>
							{$format('button_delete', { default: 'Delete' })}
						</button>
					</div>
				{/if}
			{:else}
				<div class="empty-state">
					<i class="fal fa-couch empty-state__icon" aria-hidden="true"></i>
					<p class="empty-state__text">
						{$format('presence_furniture_no_placed', { default: 'No furniture placed' })}
					</p>
					<p class="empty-state__hint">
						{$format('presence_furniture_no_placed_hint', {
							default: 'Go to "Add" to place items on the grid.',
						})}
					</p>
				</div>
			{/if}

			<!-- ── ADD TAB ────────────────────────────────────────────────────── -->
		{:else if activeTab === 'add'}
			{#if catalogLoading}
				<div class="loading-state">
					<i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
				</div>
			{:else if selectedGroup === null}
				<!-- Category grid -->
				<div class="category-grid">
					{#each catalogGroups as group}
						<button
							class="category-card"
							on:click={() => handleGroupClick(group)}
							aria-label={group.group}
						>
							{#if group.items[0]}
								<img
									src="./images/furnitures/{group.items[0].src}"
									alt=""
									class="category-card__img"
								/>
							{/if}
							<span class="category-card__label">{group.group}</span>
						</button>
					{/each}
				</div>
			{:else}
				<!-- Items in selected group -->
				<div class="items-panel">
					<button class="back-btn" on:click={handleBackClick} aria-label="Back to categories">
						<i class="fas fa-chevron-left" aria-hidden="true"></i>
						{selectedGroup.group}
					</button>
					<div class="items-grid">
						{#each selectedGroup.items as item}
							<button
								class="item-card"
								on:click={() => handleItemClick(item.src, item.name)}
								aria-label="Place {item.name}"
							>
								<img
									src="./images/furnitures/{item.src}"
									alt={item.name}
									class="item-card__img"
								/>
								<span class="item-card__label">{item.name}</span>
							</button>
						{/each}
					</div>
				</div>
			{/if}
		{/if}
	</div>

	<!-- Footer -->
	<div class="furnitures__footer">
		<button class="footer-link" on:click={toggleFurnituresVisibility}>
			<i class="fas {$furnituresVisible ? 'fa-eye-slash' : 'fa-eye'}" aria-hidden="true"></i>
			{$furnituresVisible
				? $format('presence_furniture_hide', { default: 'Hide furniture' })
				: $format('presence_furniture_show', { default: 'Show furniture' })}
		</button>
		{#if $placedFurnitures.length > 0}
			<button class="footer-link footer-link--danger" on:click={() => (showResetModal = true)}>
				<i class="fas fa-trash" aria-hidden="true"></i>
				{$format('presence_furniture_reset_all', { default: 'Reset all' })}
			</button>
		{/if}
	</div>
</div>

<!-- Reset confirmation modal -->
<Modal
	on:close={() => (showResetModal = false)}
	showModal={showResetModal}
	showCloseButton={true}
	title={$format('presence_furniture_reset_modal_title', { default: 'Reset Furniture' })}
	backdropClose={true}
>
	<svelte:fragment slot="content">
		<p class="modal-text">
			{$format('presence_furniture_reset_modal_body', {
				default: 'Are you sure you want to remove all placed furniture?',
			})}
		</p>
	</svelte:fragment>
	<div class="d-flex justify-content-end" slot="footer">
		<Button class="mr-s" variant="secondary" size="m" on:click={() => (showResetModal = false)}>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button variant="danger" size="m" on:click={confirmReset}
			>{capFirstChar($format('button_reset', { default: 'Reset' }))}</Button
		>
	</div>
</Modal>

<style>
	.furnitures {
		display: flex;
		flex-direction: column;
		gap: 0;
	}

	.furnitures__content {
		padding: 0 8px;
		min-height: 160px;
	}

	/* ── Placed list ─────────────────────────────────────────────────────────── */
	.placed-list {
		list-style: none;
		margin: 0 0 8px;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 3px;
		max-height: 200px;
		overflow-y: auto;
	}

	.placed-item {
		display: flex;
		align-items: center;
		gap: 10px;
		width: 100%;
		padding: 8px 10px;
		background: #303235;
		border: 1px solid transparent;
		border-radius: 8px;
		cursor: pointer;
		text-align: left;
		transition:
			background 0.1s,
			border-color 0.1s;
	}

	.placed-item:hover {
		background: #3a3d41;
	}

	.placed-item--selected {
		background: #1e2d45;
		border-color: #2491ff;
	}

	.placed-item__thumb {
		width: 32px;
		height: 32px;
		flex-shrink: 0;
		background: rgba(255, 255, 255, 0.06);
		border-radius: 6px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.placed-item__img {
		width: 22px;
		height: 22px;
		object-fit: contain;
		filter: brightness(1.3);
	}

	.placed-item__name {
		flex: 1;
		font-size: 13px;
		color: #eaeaea;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.placed-item__rotation {
		font-size: 11px;
		color: #6b6d72;
		flex-shrink: 0;
	}

	/* ── Selected item actions ───────────────────────────────────────────────── */
	.selected-actions {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 6px 4px;
		border-top: 1px solid rgba(255, 255, 255, 0.06);
	}

	.selected-actions__label {
		flex: 1;
		font-size: 12px;
		color: #a1a2a2;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.action-btn {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		padding: 5px 10px;
		background: rgba(255, 255, 255, 0.07);
		border: 1px solid rgba(255, 255, 255, 0.1);
		border-radius: 6px;
		color: #b8b9ba;
		font-size: 12px;
		cursor: pointer;
		transition:
			background 0.12s,
			color 0.12s;
		flex-shrink: 0;
	}

	.action-btn:hover {
		background: rgba(255, 255, 255, 0.14);
		color: #fff;
	}

	.action-btn--danger:hover {
		background: rgba(220, 53, 69, 0.18);
		border-color: rgba(220, 53, 69, 0.4);
		color: #dc3545;
	}

	/* ── Empty / loading state ───────────────────────────────────────────────── */
	.empty-state,
	.loading-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 6px;
		padding: 28px 16px;
		text-align: center;
	}

	.loading-state {
		font-size: 22px;
		color: #5f6063;
	}

	.empty-state__icon {
		font-size: 32px;
		color: #4a4d52;
	}

	.empty-state__text {
		font-size: 14px;
		font-weight: 500;
		color: #a1a2a2;
		margin: 0;
	}

	.empty-state__hint {
		font-size: 12px;
		color: #5f6063;
		margin: 0;
	}

	/* ── Category grid ───────────────────────────────────────────────────────── */
	.category-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 6px;
	}

	.category-card {
		background: #303235;
		border: none;
		border-radius: 8px;
		padding: 8px 4px 6px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 5px;
		cursor: pointer;
		transition: background 0.1s;
		text-align: center;
	}

	.category-card:hover {
		background: #3a3d41;
	}

	.category-card:focus-visible {
		outline: 2px solid #2491ff;
		outline-offset: -2px;
	}

	.category-card__img {
		width: 32px;
		height: 32px;
		object-fit: contain;
		filter: brightness(1.3);
	}

	.category-card__label {
		font-size: 10px;
		font-weight: 500;
		color: #a1a2a2;
		line-height: 1.3;
	}

	/* ── Items panel (drill-down) ────────────────────────────────────────────── */
	.items-panel {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.back-btn {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: none;
		border: none;
		color: #a1a2a2;
		font-size: 13px;
		cursor: pointer;
		padding: 2px 0;
		font-weight: 600;
		transition: color 0.12s;
	}

	.back-btn:hover {
		color: #eaeaea;
	}

	.items-grid {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		max-height: 220px;
		overflow-y: auto;
	}

	.item-card {
		width: 60px;
		background: #303235;
		border: none;
		border-radius: 8px;
		padding: 7px 4px 5px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
		cursor: pointer;
		transition: background 0.1s;
		text-align: center;
	}

	.item-card:hover {
		background: #3a3d41;
	}

	.item-card:focus-visible {
		outline: 2px solid #2491ff;
		outline-offset: -2px;
	}

	.item-card__img {
		width: 32px;
		height: 32px;
		object-fit: contain;
		filter: brightness(1.3) contrast(1.1);
	}

	.item-card__label {
		font-size: 9px;
		color: #a1a2a2;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		width: 100%;
	}

	/* ── Footer ──────────────────────────────────────────────────────────────── */
	.furnitures__footer {
		display: flex;
		align-items: center;
		gap: 16px;
		padding: 10px 12px 4px;
		border-top: 1px solid rgba(255, 255, 255, 0.06);
		margin-top: 10px;
	}

	.footer-link {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: none;
		border: none;
		color: #a1a2a2;
		font-size: 13px;
		cursor: pointer;
		padding: 0;
		transition: color 0.12s;
	}

	.footer-link:hover {
		color: #eaeaea;
	}

	.footer-link--danger:hover {
		color: #dc3545;
	}

	.modal-text {
		font-size: 14px;
		color: #eaeaea;
		margin: 0;
	}
</style>
